import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/** Reusable DOM selectors */
const selectors = {
  OPTION: '.variant-option[data-metafield-key]',
  RADIO: 'input[type="radio"].variant-option__metafield-select',
  SELECT: 'select.variant-option__metafield-select',
  SELECT_FIELD: 'select-field.variant-option__metafield-select',
  VARIANT_ENTRY: '[data-variant-metafields-map] [data-variant-id]',
  SELECTED_VALUE: '[data-selected-value]',
};

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets
 */

/**
 * Custom element that manages variant selection on PDP.
 *
 * Two modes:
 * - **Standard** — uses Shopify's native option_values (fieldsets with `data-option-value-id`)
 * - **Custom** (`variant-picker--custom`) — metafield-based picker with cascading selection
 *
 * @extends Component<VariantPickerRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {number[][]} */
  #checkedIndices = [];
  /** @type {HTMLInputElement[][]} */
  #radios = [];

  get #isCustom() {
    return this.classList.contains('variant-picker--custom');
  }

  get #isCascading() {
    return this.#isCustom && this.dataset.style === 'dropdowns';
  }

  connectedCallback() {
    super.connectedCallback();

    for (const fieldset of /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || [])) {
      const radios = /** @type {HTMLInputElement[]} */ ([...fieldset.querySelectorAll('input')]);
      this.#radios.push(radios);
      const idx = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (idx !== -1) this.#checkedIndices.push([idx]);
    }

    this.addEventListener('change', this.#onVariantChange);
    this.#applyInitialAvailability();
  }

  /**
   * Applies the initial availability/value pass.
   *
   * Runs synchronously first so container-level state (`variant-option--disabled`,
   * fieldset/native-select `disabled`) paints immediately on load — this does not
   * depend on nested select-field components being upgraded.
   *
   * `variant-picker.js` loads before `select.js`, so on initial connect the nested
   * select-field components aren't upgraded yet and the `field:*` events that drive
   * their internal state (trigger/option disabled, value sync) would be dropped.
   * Re-run the (idempotent) pass once `select-field` is defined so those events reach
   * attached listeners.
   */
  #applyInitialAvailability() {
    const run = () => {
      if (this.#isCustom) {
        if (this.#isCascading) {
          // Preserve the server-rendered selection when the page is opened with a
          // specific variant in the URL (?variant=...) so the options stay
          // selected on load. Without ?variant= we mirror the change flow: keep
          // the first option, reset the rest to "Please Select", then let
          // availability lock the disabled ones.
          const hasVariantParam = new URL(window.location.href).searchParams.has('variant');
          const [first] = this.#getOptionContainers();
          if (first && !hasVariantParam) {
            this.#resetSubsequentOptions(first.dataset.metafieldKey || '');
          }
          this.#updateCascadingAvailability();
        } else {
          this.#updateStandardAvailability();
        }
      }
      this.#applyUrlOptionParam();
    };

    run();

    if (this.querySelector(selectors.SELECT_FIELD) && typeof customElements?.whenDefined === 'function') {
      customElements.whenDefined('select-field').then(() => {
        if (this.isConnected) run();
      });
    }
  }

  // ─── URL ?option1= Handling ───────────────────────────────────────────

  /**
   * Pre-selects the first option from ?option1= URL param and resets the rest.
   * Used when navigating from a series landing page lineup card.
   */
  #applyUrlOptionParam() {
    const value = new URL(window.location.href).searchParams.get('option1');
    if (!value) return;

    this.#isCustom
      ? this.#applyMetafieldLineupParam(value)
      : this.#applyStandardLineupParam(value);

    this.closest('.shopify-section')
      ?.querySelectorAll('[data-variant-update="variant-title"]')
      .forEach((el) => el.setAttribute('hidden', ''));
  }

  /** @param {string} value */
  #applyMetafieldLineupParam(value) {
    for (const container of this.#getOptionContainers()) {
      if (container.dataset.metafieldKey === 'option1') {
        this.#setOptionValue(container, value);
      }
    }
    if (this.#isCascading) {
      this.#resetSubsequentOptions('option1');
      this.#updateCascadingAvailability();
    }
  }

  /** @param {string} value */
  #applyStandardLineupParam(value) {
    const form = this.querySelector('.variant-picker__form');
    if (!form) return;

    const containers = form.querySelectorAll(
      ':scope > fieldset.variant-option, :scope > div.variant-option'
    );

    containers.forEach((container, index) => {
      const select = container.querySelector('select.variant-option__select');

      if (index === 0) {
        // Select the matching value in the first option
        if (select instanceof HTMLSelectElement) {
          const target = [...select.options].find((option) => option.value === value);
          if (target) {
            for (const option of select.options) option.removeAttribute('selected');
            target.setAttribute('selected', 'selected');
            select.value = value;
          }
        } else {
          container.querySelectorAll('input[type="radio"]').forEach((radioEl) => {
            const radio = /** @type {HTMLInputElement} */ (radioEl);
            const match = radio.value === value;
            radio.checked = match;
            radio.dataset.currentChecked = match ? 'true' : 'false';
          });
        }
      } else {
        // Reset all subsequent options to "Please Select"
        if (select instanceof HTMLSelectElement) {
          this.#insertPlaceholder(select);
        } else {
          container.querySelectorAll('input[type="radio"]').forEach((radioEl) => {
            const radio = /** @type {HTMLInputElement} */ (radioEl);
            radio.checked = false;
            radio.dataset.currentChecked = 'false';
          });
          const label = container.querySelector('.variant-option__swatch-value');
          if (label) label.textContent = 'Please Select';
        }
      }
    });
  }

  // ─── Change Event Router ──────────────────────────────────────────────

  /** @param {Event} event */
  #onVariantChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target) return;

    // Ignore non-variant inputs (e.g. fit guide tabs inside the same form)
    const isVariant =
      target.classList.contains('variant-option__metafield-select') ||
      target.classList.contains('variant-option__select') ||
      target.closest('[data-option-value-id]') ||
      target.closest(selectors.SELECT_FIELD);
    if (!isVariant) return;

    this.#isCustom
      ? this.#handleCascadingChange(target)
      : this.#handleStandardChange(target);
  };

  // ─── Custom Metafield Picker (cascading) ──────────────────────────────

  /** @param {HTMLElement} target */
  #handleCascadingChange(target) {
    const container = /** @type {HTMLElement | null} */ (target.closest(selectors.OPTION));
    if (container && this.#isCascading) this.#resetSubsequentOptions(container.dataset.metafieldKey || '');

    this.updateSelectedOption(target);
    this.#isCascading ? this.#updateCascadingAvailability() : this.#updateStandardAvailability();

    // Only fetch when every option has a value
    const selections = this.#getSelectedMetafieldValues();
    if (Object.keys(selections).length < this.querySelectorAll(selectors.OPTION).length) {
      this.dispatchEvent(new VariantSelectedEvent({ id: '' }));
      return;
    }

    const variantId = this.#findMatchingVariantId(selections);
    if (!variantId) return;

    this.dispatchEvent(new VariantSelectedEvent({ id: variantId }));
    this.fetchUpdatedSection(this.#buildVariantUrl(variantId), false);
    this.#pushUrlState(variantId, target);
  }

  // ─── Standard Shopify Picker ──────────────────────────────────────────

  /** @param {HTMLElement} target */
  #handleStandardChange(target) {
    const selectedOption =
      target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;
    if (!selectedOption) return;

    const matchedVariantId = this.#findMatchingVariantId(this.#getSelectedMetafieldValues());

    this.updateSelectedOption(target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? selectedOption.dataset.variantId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // Fetch updated section
    if (selectedOption.dataset.optionValueId) {
      this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);
    } else if (matchedVariantId) {
      this.fetchUpdatedSection(this.#buildVariantUrl(matchedVariantId), loadsNewProduct);
    }

    // Update browser URL
    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || matchedVariantId;

    if (isOnProductPage) {
      variantId ? url.searchParams.set('variant', variantId) : url.searchParams.delete('variant');
    }
    if (loadsNewProduct && newUrl) url.pathname = newUrl;
    if (url.href !== window.location.href) {
      requestYieldCallback(() => history.replaceState({}, '', url.toString()));
    }
  }

  // ─── Visual State Updates ─────────────────────────────────────────────

  /**
   * Syncs the visual state of a changed input (pill animation, checked attrs).
   * @param {string | Element} target
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const el = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!el) throw new Error('Target element not found');
      target = el;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldset = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || [])[fieldsetIndex];
        const indices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && indices && fieldset) {
          const [cur, prev] = indices;
          if (cur !== undefined && radios[cur]) radios[cur].dataset.previousChecked = 'false';
          if (prev !== undefined && radios[prev]) radios[prev].dataset.previousChecked = 'false';

          indices.unshift(inputIndex);
          indices.length = Math.min(indices.length, 2);

          const [newCur, newPrev] = indices;
          if (newCur !== undefined && radios[newCur]) {
            radios[newCur].dataset.currentChecked = 'true';
            fieldset.style.setProperty('--pill-width-current', `${radios[newCur].parentElement?.offsetWidth || 0}px`);
          }
          if (newPrev !== undefined && radios[newPrev]) {
            radios[newPrev].dataset.previousChecked = 'true';
            radios[newPrev].dataset.currentChecked = 'false';
            fieldset.style.setProperty('--pill-width-previous', `${radios[newPrev].parentElement?.offsetWidth || 0}px`);
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const matchedOption = [...target.options].find((option) => option.value === target.value);
      if (!matchedOption) throw new Error('Option not found');
      for (const option of target.options) option.removeAttribute('selected');
      matchedOption.setAttribute('selected', 'selected');
    }
  }

  // ─── Section Fetching & Morphing ──────────────────────────────────────

  /**
   * Builds the request URL for standard pickers (option_values-based).
   * @param {HTMLElement} selectedOption
   * @param {string | null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   * @returns {string}
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;

    const params = [];
    const view = getViewParameterValue();
    if (view) params.push(`view=${view}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      const values = this.selectedOptionsValues.length ? sourceSelectedOptionsValues : [selectedOption.dataset.optionValueId];
      params.push(`option_values=${values.join(',')}`);
    }

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      productUrl = productUrl?.split('?')[0];
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches updated HTML and morphs the variant picker (or entire main).
   * @param {string} requestUrl
   * @param {boolean} [shouldMorphMain]
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((text) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(text, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const json = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
        if (!json) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(json), this.#isCustom ? '' : (this.selectedOptionId ?? ''), {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }

        // Morph strategy:
        // - Sibling sections (variant-aware metafield content like hero, content-carousel,
        //   photo-strip, etc.) — morph entire `.shopify-section` by Shopify-generated id.
        // - Picker's own section — morph only individual `[data-variant-update]` elements
        //   (sku, description, etc.). Whole-section morph would clobber `<media-gallery>`
        //   because its VariantUpdateEvent handler uses replaceWith(), which moves the node
        //   out of the source HTML before this loop runs.
        const isPdpPicker =
          this.dataset.templateProductMatch === 'true' &&
          !this.closest('product-card') &&
          !this.closest('quick-add-dialog');
        const morphScope = isPdpPicker ? document : this.closest('.shopify-section');
        const pickerSection = this.closest('.shopify-section');
        const mainEl = document.querySelector('main');

        morphScope
          ?.querySelectorAll('.shopify-section')
          .forEach((el) => {
            if (el === pickerSection) return;
            // Skip header/footer (they live outside <main>) and any other sections
            // mounted outside the main content area (modals, drawers, etc.).
            if (!mainEl || !mainEl.contains(el)) return;
            if (isPdpPicker && (el.closest('product-card') || el.closest('quick-add-dialog'))) return;
            if (!el.id) return;
            const newEl = html.getElementById(el.id);
            if (newEl) morph(el, newEl);
          });

        // Inside the picker's own section: morph individual marked elements only.
        // Pair them by position within the matching section in `html` (not by global
        // first-match) — multiple elements can share the same marker (e.g. several
        // `_product-short-description` blocks all use `data-variant-update="short-description"`).
        // A global lookup would morph every duplicate from the same first source node,
        // and morph() may mutate the source as it walks, leaving later matches with stale
        // or emptied content.
        const newPickerSection = pickerSection?.id ? html.getElementById(pickerSection.id) : null;
        if (pickerSection && newPickerSection) {
          const oldEls = pickerSection.querySelectorAll('[data-variant-update]');
          const newEls = newPickerSection.querySelectorAll('[data-variant-update]');
          oldEls.forEach((el, i) => {
            const newEl = newEls[i];
            if (!newEl) return;
            if (el.getAttribute('data-variant-update') !== newEl.getAttribute('data-variant-update')) return;
            morph(el, newEl);
          });
        }

        // Restore visibility of variant-title elements that may have been hidden
        // by the ?option1= URL param flow on initial load.
        morphScope
          ?.querySelectorAll('[data-variant-update="variant-title"]')
          .forEach((el) => el.removeAttribute('hidden'));

        // Re-apply availability after morph (connectedCallback won't re-fire)
        if (this.isConnected && this.#isCustom) {
          this.#isCascading ? this.#updateCascadingAvailability() : this.#updateStandardAvailability();
        }
        const container = window.yotpoWidgetsContainer;
        if (container && typeof container.initWidgets === 'function') {
          container.initWidgets();
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') console.error(err);
      });
  }

  /**
   * @typedef {{ id: string, url: string }} NewProduct
   */

  /**
   * Morphs the variant picker with fresh HTML.
   * @param {Document} doc
   * @returns {NewProduct | undefined}
   */
  updateVariantPicker(doc) {
    /** @type {NewProduct | undefined} */
    let newProduct;
    const source = doc.querySelector(this.tagName.toLowerCase());
    if (!source) throw new Error('No new variant picker source found');

    if (source instanceof HTMLElement) {
      const { productId, productUrl } = source.dataset;
      if (productId && productUrl && this.dataset.productId !== productId) {
        newProduct = { id: productId, url: productUrl };
      }
      this.dataset.productId = productId;
      this.dataset.productUrl = productUrl;
    }

    morph(this, source);
    return newProduct;
  }

  /**
   * Morphs the entire `<main>` element (combined listings).
   * @param {Document} doc
   */
  updateMain(doc) {
    const main = document.querySelector('main');
    const newMain = doc.querySelector('main');
    if (!main || !newMain) throw new Error('No new main source found');
    morph(main, newMain);
  }

  // ─── Getters ──────────────────────────────────────────────────────────

  /** @returns {HTMLInputElement | HTMLOptionElement | undefined} */
  get selectedOption() {
    const el = this.querySelector('select option[selected], fieldset input:checked');
    return el instanceof HTMLInputElement || el instanceof HTMLOptionElement ? el : undefined;
  }

  /** @returns {string | undefined} */
  get selectedOptionId() {
    const id = this.selectedOption?.dataset.optionValueId;
    if (this.selectedOption && !id) throw new Error('No option value ID found');
    return id;
  }

  /** @returns {string[]} */
  get selectedOptionsValues() {
    return /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('select option[selected], fieldset input:checked'),
    ]).map((el) => {
      if (!el.dataset.optionValueId) throw new Error('No option value ID found');
      return el.dataset.optionValueId;
    });
  }

  // ─── Cascading Availability Engine ────────────────────────────────────

  /**
   * Standard availability: processes options top-to-bottom using only confirmed
   * prior selections. If the current value for an option is incompatible with
   * confirmed prior selections, it is cleared automatically. This avoids the
   * deadlock that arises from mutual cross-option filtering on non-cartesian
   * variant combinations (e.g. only A1/B1 and A2/B2).
   */
  #updateStandardAvailability() {
    const variants = this.#getVariantsFromMap();
    const containers = this.#getOptionContainers();
    const selected = this.#getSelectedMetafieldValues();

    /** @type {Record<string, string>} Confirmed selections used to filter later options */
    const confirmed = {};

    for (const container of containers) {
      const key = container.dataset.metafieldKey || '';

      const available = Object.keys(confirmed).length === 0
        ? null
        : /** @type {Set<string>} */ (new Set(
            variants
              .filter((v) => Object.entries(confirmed).every(([k, val]) => v.metafields[k] === val))
              .map((v) => v.metafields[key])
              .filter(/** @returns {str is string} */ (str) => !!str)
          ));

      const currentValue = selected[key];

      if (available && currentValue && !available.has(currentValue)) {
        // Clear value that is no longer reachable given confirmed prior selections
        this.#clearOptionValue(container);
        delete selected[key];
      } else if (currentValue) {
        confirmed[key] = currentValue;
      }

      this.#applyValueAvailability(container, available);
    }

    this.#updateSelectedValueLabels();
  }

  /**
   * Cascading availability: options must be filled top-to-bottom.
   * - Option 1 is always fully enabled.
   * - Option N is locked until 1..N-1 are all selected.
   * - Available values for N come from variants matching selections 1..N-1.
   */
  #updateCascadingAvailability() {
    const variants = this.#getVariantsFromMap();
    const containers = this.#getOptionContainers();
    const selected = this.#getSelectedMetafieldValues();

    /** @type {Record<string, string>} */
    const prior = {};
    let chainComplete = true;

    for (let i = 0; i < containers.length; i++) {
      const container = containers[i];
      if (!container) continue;
      const key = container.dataset.metafieldKey || '';
      const value = selected[key];

      if (i === 0) {
        this.#setOptionEnabled(container, true);
        this.#applyValueAvailability(container, null); // all enabled
        value ? (prior[key] = value) : (chainComplete = false);
        continue;
      }

      if (!chainComplete) {
        this.#setOptionEnabled(container, false);
        continue;
      }

      this.#setOptionEnabled(container, true);

      const available = /** @type {Set<string>} */ (new Set(
        variants
          .filter((v) => Object.entries(prior).every(([k, val]) => v.metafields[k] === val))
          .map((v) => v.metafields[key])
          .filter(/** @returns {str is string} */ (str) => !!str)
      ));

      this.#applyValueAvailability(container, available);

      if (value && available.has(value)) {
        prior[key] = value;
      } else {
        chainComplete = false;
      }
    }

    this.#updateSelectedValueLabels();
  }

  /**
   * Enables/disables an entire option group (fieldset or dropdown wrapper).
   * @param {HTMLElement} container
   * @param {boolean} enabled
   */
  #setOptionEnabled(container, enabled) {
    container.classList.toggle('variant-option--disabled', !enabled);
    if (container instanceof HTMLFieldSetElement) container.disabled = !enabled;
    const selectEl = container.querySelector('select');
    if (selectEl) /** @type {HTMLSelectElement} */ (selectEl).disabled = !enabled;
    for (const selectFieldEl of container.querySelectorAll(selectors.SELECT_FIELD)) {
      this.#dispatchToSelectField(selectFieldEl, 'field:setEnabled', { enabled });
    }
  }

  /**
   * Sets per-value disabled state. Pass `null` to enable all values.
   * @param {HTMLElement} container
   * @param {Set<string> | null} available
   */
  #applyValueAvailability(container, available) {
    for (const input of container.querySelectorAll(selectors.RADIO)) {
      const el = /** @type {HTMLInputElement} */ (input);
      const isAvailable = !available || available.has(el.value);
      el.disabled = !isAvailable;
      el.closest('.variant-option__swatch-label')
        ?.classList.toggle('variant-option__swatch-label--disabled', !isAvailable);
    }
    for (const selectEl of container.querySelectorAll(selectors.SELECT)) {
      for (const option of /** @type {HTMLSelectElement} */ (selectEl).options) {
        if (option.value) option.disabled = available ? !available.has(option.value) : false;
      }
    }
    for (const selectFieldEl of container.querySelectorAll(selectors.SELECT_FIELD)) {
      this.#dispatchToSelectField(selectFieldEl, 'field:setAvailability', {
        available: available ? [...available] : null,
      });
    }
  }

  #updateSelectedValueLabels() {
    for (const el of this.querySelectorAll(selectors.SELECTED_VALUE)) {
      const checked = el.closest('[data-metafield-key]')?.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement) el.textContent = checked.value;
    }
  }

  // ─── Option Reset & Value Helpers ─────────────────────────────────────

  /**
   * Resets every option after `changedKey` to "Please Select".
   * @param {string} changedKey
   */
  #resetSubsequentOptions(changedKey) {
    const containers = this.#getOptionContainers();
    const idx = containers.findIndex((container) => container.dataset.metafieldKey === changedKey);
    if (idx === -1) return;
    for (let i = idx + 1; i < containers.length; i++) {
      const container = containers[i];
      if (container) this.#clearOptionValue(container);
    }
  }

  /**
   * Clears the selected value in a single option container.
   * @param {HTMLElement} container
   */
  #clearOptionValue(container) {
    for (const radio of container.querySelectorAll(selectors.RADIO)) /** @type {HTMLInputElement} */ (radio).checked = false;
    for (const selectEl of container.querySelectorAll(selectors.SELECT)) this.#insertPlaceholder(/** @type {HTMLSelectElement} */ (selectEl));
    for (const selectFieldEl of container.querySelectorAll(selectors.SELECT_FIELD)) this.#setSelectFieldValue(/** @type {HTMLElement} */ (selectFieldEl), '');
    const label = container.querySelector(selectors.SELECTED_VALUE);
    if (label) label.textContent = 'Please Select';
  }

  /**
   * Programmatically selects a value in an option container.
   * @param {HTMLElement} container
   * @param {string} value
   */
  #setOptionValue(container, value) {
    const selectFieldEl = container.querySelector(selectors.SELECT_FIELD);
  
    if (selectFieldEl) {
      this.#setSelectFieldValue(/** @type {HTMLElement} */ (selectFieldEl), value);
      return;
    }
    const select = container.querySelector(selectors.SELECT);
    if (select instanceof HTMLSelectElement) {
      const target = [...select.options].find((option) => option.value === value);
      if (target) {
        for (const option of select.options) option.removeAttribute('selected');
        target.setAttribute('selected', 'selected');
        select.value = value;
      }
      return;
    }
    for (const radio of container.querySelectorAll(selectors.RADIO)) {
      /** @type {HTMLInputElement} */ (radio).checked = /** @type {HTMLInputElement} */ (radio).value === value;
    }
    const label = container.querySelector(selectors.SELECTED_VALUE);
    if (label) label.textContent = value;
  }

  /**
   * Syncs a select-field web component to a given value without dispatching a change event.
   * The select-field owns its own UI sync (label, active option, _selected class) — we
   * only hand it the value via the `field:setValue` event with `silent: true`.
   * @param {HTMLElement} selectFieldEl
   * @param {string} value
   */
  #setSelectFieldValue(selectFieldEl, value) {
    this.#dispatchToSelectField(selectFieldEl, 'field:setValue', { value, silent: true });
  }

  /**
   * Dispatches a `field:*` control event to a select-field web component, which owns
   * all of its own state management. The picker never mutates select-field internals
   * directly — it only signals intent.
   * @param {Element} selectFieldEl
   * @param {string} type
   * @param {Record<string, unknown>} detail
   */
  #dispatchToSelectField(selectFieldEl, type, detail) {
    selectFieldEl.dispatchEvent(new CustomEvent(type, { detail }));
  }

  /**
   * Inserts (or reuses) a disabled "Please Select" placeholder at the top of a `<select>`.
   * @param {HTMLSelectElement} select
   */
  #insertPlaceholder(select) {
    let placeholderOption = /** @type {HTMLOptionElement | null} */ (select.querySelector('option[data-placeholder]'));
    if (!placeholderOption) {
      placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.textContent = 'Please Select';
      placeholderOption.disabled = true;
      placeholderOption.dataset.placeholder = 'true';
      select.insertBefore(placeholderOption, select.firstChild);
    }
    for (const option of select.options) option.removeAttribute('selected');
    select.selectedIndex = 0;
  }

  // ─── Data Helpers ─────────────────────────────────────────────────────

  /** @returns {HTMLElement[]} Ordered list of option containers */
  #getOptionContainers() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll(selectors.OPTION)]);
  }

  /**
   * Currently selected metafield values (key → value).
   * Skips unchecked radios and empty dropdowns.
   * @returns {Record<string, string>}
   */
  #getSelectedMetafieldValues() {
    /** @type {Record<string, string>} */
    const result = {};
    for (const el of this.querySelectorAll('.variant-option__metafield-select')) {
      if (el instanceof HTMLInputElement && el.type === 'radio') {
        if (el.checked) result[el.dataset.metafieldKey || ''] = el.value;
      } else if (el instanceof HTMLSelectElement && el.value) {
        result[el.dataset.metafieldKey || ''] = el.value;
      } else if (el.tagName === 'SELECT-FIELD') {
        const key = /** @type {HTMLElement | null} */ (el.closest('[data-metafield-key]'))?.dataset.metafieldKey || '';
        const val = /** @type {HTMLInputElement | null} */ (el.querySelector('input[type="hidden"]'))?.value || '';
        if (key && val) result[key] = val;
      }
    }
    return result;
  }

  /**
   * Parses the hidden variant metafields map in the DOM.
   * @returns {Array<{id: string, available: boolean, metafields: Record<string, string>}>}
   */
  #getVariantsFromMap() {
    return [...this.querySelectorAll(selectors.VARIANT_ENTRY)].map((el) => {
      /** @type {Record<string, string>} */
      const metafields = {};
      for (const mf of el.querySelectorAll('[data-metafield-key]')) {
        const metafieldEl = /** @type {HTMLElement} */ (mf);
        metafields[metafieldEl.dataset.metafieldKey || ''] = metafieldEl.dataset.metafieldValue || '';
      }
      const variantEl = /** @type {HTMLElement} */ (el);
      return { id: variantEl.dataset.variantId || '', available: variantEl.dataset.variantAvailable !== 'false', metafields };
    });
  }

  /**
   * Finds the first variant whose metafields match all given selections.
   * @param {Record<string, string>} selections
   * @returns {string | null} variant ID or null
   */
  #findMatchingVariantId(selections) {
    const entries = Object.entries(selections);
    for (const el of this.querySelectorAll(selectors.VARIANT_ENTRY)) {
      const mfEls = el.querySelectorAll('[data-metafield-key]');
      const allFieldsMatch = entries.every(([key, value]) => {
        const mf = [...mfEls].find((m) => /** @type {HTMLElement} */ (m).dataset.metafieldKey === key);
        return mf && /** @type {HTMLElement} */ (mf).dataset.metafieldValue === value;
      });
      if (allFieldsMatch) return /** @type {HTMLElement} */ (el).dataset.variantId || null;
    }
    return null;
  }

  /**
   * Builds a variant URL with view param preserved.
   * @param {string} variantId
   * @returns {string}
   */
  #buildVariantUrl(variantId) {
    const base = this.dataset.productUrl?.split('?')[0];
    const view = getViewParameterValue();
    return `${base}?variant=${variantId}${view ? `&view=${view}` : ''}`;
  }

  /**
   * Updates browser URL to reflect the selected variant.
   * @param {string} variantId
   * @param {HTMLElement} target
   */
  #pushUrlState(variantId, target) {
    const onPDP =
      this.dataset.templateProductMatch === 'true' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');
    if (!onPDP) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    requestYieldCallback(() => history.replaceState({}, '', url.toString()));
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
